import type {
  IFormatOwnerNameProps,
  IFormatUserDisplayValueProps,
  IGetUserDisplayPartsProps,
  IGetUserHandleProps,
  IUserDisplayParts,
} from "../types/user";

// NOTE: NSAI-3491 — an em dash is what the rest of the product prints for a
// value it does not have (see the preview modals), so an unowned row reads the
// same way as any other blank cell.
const OWNER_PLACEHOLDER = "—" as const;

// NOTE: NSAI-7736 — what identifies a person besides their name: the email,
// else the login name their organization's IdP signs them in with (a member
// whose directory gives them no address), else "". One place, so every list,
// picker and menu falls back the same way instead of printing a blank line.
export const getUserHandle = ({
  email,
  username,
}: IGetUserHandleProps): string => email?.trim() || username?.trim() || "";

export const getUserDisplayParts = ({
  firstName,
  lastName,
  email,
  username,
}: IGetUserDisplayPartsProps): IUserDisplayParts => {
  const normalizedFirstName = firstName?.trim() ?? "";
  const normalizedLastName = lastName?.trim() ?? "";
  const normalizedEmail = email?.trim() ?? "";
  const fullName = [normalizedFirstName, normalizedLastName]
    .filter(Boolean)
    .join(" ");

  return {
    fullName,
    email: normalizedEmail,
    handle: getUserHandle({ email, username }),
  };
};

export const formatUserDisplayValue = ({
  firstName,
  lastName,
  email,
  username,
  defaultPlaceholder,
}: IFormatUserDisplayValueProps): string => {
  const { fullName, handle } = getUserDisplayParts({
    firstName,
    lastName,
    email,
    username,
  });
  const normalizedDefaultPlaceholder = defaultPlaceholder?.trim() || "-";

  if (fullName && handle) {
    return `${fullName} (${handle})`;
  }

  if (fullName) {
    return fullName;
  }

  return handle || normalizedDefaultPlaceholder;
};

// NOTE: NSAI-3491 — the owner label for a list row: the full name when the
// account has one, the email when it does not, and the placeholder when the
// entity has no owner at all (a probe created before owners were recorded, a
// seeded global probe). Unlike formatUserDisplayValue it never appends the
// email in parentheses — an Owner column has no room for it, and the name
// alone is what identifies the person to a teammate.
export const formatOwnerName = ({
  owner,
  placeholder = OWNER_PLACEHOLDER,
}: IFormatOwnerNameProps): string => {
  if (!owner) return placeholder;

  const { fullName, handle } = getUserDisplayParts({
    firstName: owner.firstName,
    lastName: owner.lastName,
    email: owner.email,
    username: owner.username,
  });

  return fullName || handle || placeholder;
};
